import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { useApp } from '@/lib/context';
import { getTranslations } from '@/lib/translations';
import { ProfileType } from '@shared/schema';

interface Step3CategoriesProps {
  form: UseFormReturn<any>;
}

// Stesse 5 categorie del travel
const PROFILE_TYPES = [
  {
    id: ProfileType.COUPLE,
    name: "Couple",
    image: "/assets/images/gender/coppia.svg",
    gradient: "radial-gradient(circle at bottom right, rgba(227, 5, 38, 0.8) 0%, rgba(227, 5, 38, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.FEMALE,
    name: "Female",
    image: "/assets/images/gender/venus.svg",
    gradient: "radial-gradient(circle at bottom right, rgba(236, 72, 153, 0.8) 0%, rgba(236, 72, 153, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.MALE,
    name: "Male",
    image: "/assets/images/gender/mars.svg",
    gradient: "radial-gradient(circle at bottom right, rgba(21, 101, 235, 0.8) 0%, rgba(21, 101, 235, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.TRANS,
    name: "Trans",
    image: "/assets/images/gender/gender-transgender.svg",
    gradient: "radial-gradient(circle at bottom right, rgba(168, 85, 247, 0.8) 0%, rgba(168, 85, 247, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.TRAV,
    name: "Trav",
    image: "/assets/images/gender/gender-trasvesti.svg",
    gradient: "radial-gradient(circle at bottom right, rgba(192, 132, 252, 0.8) 0%, rgba(192, 132, 252, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  }
];

const getIconColorValue = (type: string): string => {
  switch (type) {
    case ProfileType.COUPLE: return '#e30526';
    case ProfileType.FEMALE: return '#ec4899';
    case ProfileType.MALE: return '#1565eb';
    case ProfileType.TRANS: return '#a855f7';
    case ProfileType.TRAV: return '#c084fc';
    default: return '#ffffff';
  }
};

const getColorFilter = (targetColor: string): string => {
  switch (targetColor) {
    case '#e30526': return 'none';
    case '#ec4899': return 'invert(44%) sepia(89%) saturate(3032%) hue-rotate(312deg) brightness(99%) contrast(101%)';
    case '#1565eb': return 'invert(38%) sepia(96%) saturate(4693%) hue-rotate(195deg) brightness(106%) contrast(101%)';
    case '#a855f7': return 'invert(29%) sepia(79%) saturate(4400%) hue-rotate(265deg) brightness(96%) contrast(101%)';
    case '#c084fc': return 'invert(33%) sepia(62%) saturate(3743%) hue-rotate(253deg) brightness(107%) contrast(107%)';
    default: return 'none';
  }
};

export function Step3Categories({ form }: Step3CategoriesProps) {
  const { user } = useApp();
  const t = getTranslations(user?.language || 'en');

  const toggleCategory = (categoryId: string) => {
    const current = form.getValues('allowedCategories');
    const newCategories = current.includes(categoryId)
      ? current.filter((c: string) => c !== categoryId)
      : [...current, categoryId];

    form.setValue('allowedCategories', newCategories, { shouldValidate: true });

    // Vibrazione tattile tramite Telegram WebApp API
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg && tg.HapticFeedback) {
        tg.HapticFeedback.impactOccurred('light');
      }
    } catch (error) {
      console.log('[SPEED CATEGORIES] Haptic feedback non disponibile:', error);
    }
  };

  const getTitle = () => {
    switch (user?.language) {
      case 'it': return 'Chi può partecipare';
      case 'es': return 'Quién puede participar';
      case 'de': return 'Wer kann teilnehmen';
      case 'pt': return 'Quem pode participar';
      case 'fr': return 'Qui peut participer';
      default: return 'Who can participate';
    }
  };

  const getSubtitle = () => {
    switch (user?.language) {
      case 'it': return 'Seleziona i tipi di profilo che possono vedere e partecipare al tuo speed date';
      case 'es': return 'Selecciona los tipos de perfil que pueden ver y participar en tu speed date';
      case 'de': return 'Wähle die Profiltypen, die an deinem Speed Date teilnehmen können';
      case 'pt': return 'Selecione os tipos de perfil que podem ver e participar do seu speed date';
      case 'fr': return 'Sélectionnez les types de profil qui peuvent voir et participer à votre speed date';
      default: return 'Select profile types that can see and join your speed date';
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-semibold tg-text mb-2">{getTitle()}</h2>
        <p className="text-sm tg-hint">{getSubtitle()}</p>
      </div>

      <FormField
        control={form.control}
        name="allowedCategories"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <div className="grid grid-cols-2 gap-3">
                {PROFILE_TYPES.map((type) => {
                  const isSelected = field.value.includes(type.id);
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => toggleCategory(type.id)}
                      className="rounded-xl transition-all duration-300 px-4 flex items-center gap-3"
                      style={{
                        background: isSelected ? type.gradient : '#252525',
                        height: '36px'
                      }}
                    >
                      <img
                        src={type.image}
                        alt={type.name}
                        className="w-4 h-4 object-contain"
                        style={{
                          filter: isSelected
                            ? (type.id === ProfileType.COUPLE ? 'none' : getColorFilter(getIconColorValue(type.id)))
                            : 'brightness(0) invert(1)'
                        }}
                      />
                      <span className={`text-xs font-semibold transition-colors ${isSelected ? 'text-white' : 'text-white/60'}`}>
                        {type.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="mt-6 p-4 rounded-lg border border-[#353535]" style={{
        background: 'radial-gradient(circle at bottom right, #1025eb 0%, var(--tg-theme-secondary-bg-color, #252525) 70%)'
      }}>
        <h3 className="font-medium tg-text mb-2 flex items-center gap-2">
          <img
            src="https://em-content.zobj.net/source/telegram/386/light-bulb_1f4a1.webp"
            alt="💡"
            className="w-5 h-5"
          />
          {user?.language === 'it' ? 'Profili Ammessi' : user?.language === 'es' ? 'Perfiles Permitidos' : user?.language === 'de' ? 'Erlaubte Profile' : user?.language === 'pt' ? 'Perfis Permitidos' : user?.language === 'fr' ? 'Profils Autorisés' : 'Allowed Profiles'}
        </h3>
        <p className="text-sm tg-hint">
          {user?.language === 'it' ? 'Seleziona quali tipi di profilo possono partecipare al tuo speed date. Questo aiuta gli utenti a sapere se sono idonei prima di iscriversi.' :
           user?.language === 'es' ? 'Selecciona qué tipos de perfil pueden participar en tu speed date. Esto ayuda a los usuarios a saber si son elegibles antes de unirse.' :
           user?.language === 'de' ? 'Wähle, welche Profiltypen an deinem Speed Date teilnehmen können. Dies hilft Nutzern zu wissen, ob sie berechtigt sind, bevor sie sich anmelden.' :
           user?.language === 'pt' ? 'Selecione quais tipos de perfil podem participar do seu speed date. Isso ajuda os usuários a saber se são elegíveis antes de se inscrever.' :
           user?.language === 'fr' ? 'Sélectionnez les types de profil qui peuvent participer à votre speed date. Cela aide les utilisateurs à savoir s\'ils sont éligibles avant de s\'inscrire.' :
           'Select which profile types can participate in your speed date. This helps users know if they\'re eligible before joining.'}
        </p>
      </div>
    </div>
  );
}